/**
 * The embeddable puzzle: one board, one puzzle, for other people's pages.
 *
 *   <iframe src="https://play.xiangqimaster.com/embed.html?lang=vi" ...>
 *
 * With no `id` it is the daily puzzle, so a club page that embeds it once has
 * a new position every day without anyone touching it. `id=` pins one puzzle
 * (a blog post about a particular tactic). `lang` is en, zh, zh-hant or vi.
 *
 * It is deliberately not the app. No account, no analytics, no consent banner,
 * no router: a page that frames us should get a puzzle and nothing that
 * watches its readers. What it does share with the app is the part that has to
 * agree -- the board, the corpus, the daily choice and the rule for a correct
 * answer (the same test PuzzleView makes) -- so a puzzle can never be right
 * here and wrong there.
 */
import { Color, coordToSquare, isCheckmate, legalMoves, moveToUci, parseFen, toFen, type Move, type Position } from "@xq/rules";
import { BoardView } from "./board.js";
import { PUZZLES, type Puzzle } from "./puzzles.js";
import { puzzleFor, today } from "./daily.js";

type EmbedLang = "en" | "zh" | "zh-hant" | "vi";

const TEXT: Record<EmbedLang, Record<string, string>> = {
  en: {
    daily: "Puzzle of the day", prompt: "Red to move. Find the winning move.",
    hint: "Hint", reset: "Reset", right: "Correct!", wrong: "Not that one. Try again.",
    more: "More puzzles", by: "Free xiangqi puzzles by",
  },
  zh: {
    daily: "每日一题", prompt: "红方先走，找出制胜的一着。",
    hint: "提示", reset: "重来", right: "正确！", wrong: "不对，再试试。",
    more: "更多题目", by: "免费象棋题目 · ",
  },
  "zh-hant": {
    daily: "每日一題", prompt: "紅方先走，找出制勝的一著。",
    hint: "提示", reset: "重來", right: "正確！", wrong: "不對，再試試。",
    more: "更多題目", by: "免費象棋題目 · ",
  },
  vi: {
    daily: "Thế cờ hôm nay", prompt: "Đỏ đi. Hãy tìm nước thắng.",
    hint: "Gợi ý", reset: "Làm lại", right: "Chính xác!", wrong: "Chưa đúng, thử lại nhé.",
    more: "Thêm thế cờ", by: "Thế cờ tướng miễn phí từ",
  },
};

/** Theme names for the two languages the corpus does not carry. */
const THEME: Record<string, { "zh-hant": string; vi: string }> = {
  "Mate in 1": { "zh-hant": "一步殺", vi: "Chiếu bí 1 nước" },
  "Mate in 2": { "zh-hant": "兩步殺", vi: "Chiếu bí 2 nước" },
  "Win material": { "zh-hant": "得子", vi: "Ăn quân" },
  "Double attack": { "zh-hant": "捉雙", vi: "Bắt đôi" },
};

/** Where each language's home is on the content site. */
const SITE_HOME: Record<EmbedLang, string> = {
  en: "https://xiangqimaster.com/",
  zh: "https://xiangqimaster.com/zh",
  "zh-hant": "https://xiangqimaster.com/zh-hant",
  vi: "https://xiangqimaster.com/vi",
};
/** The app's UI language for each widget language: Traditional reads the Chinese UI; Vietnamese has its own since 2026-10-23. */
const APP_LANG: Record<EmbedLang, string> = { en: "en", zh: "zh", "zh-hant": "zh-hant", vi: "vi" };

const params = new URLSearchParams(location.search);
const lang: EmbedLang = ((): EmbedLang => {
  const q = (params.get("lang") ?? "").toLowerCase();
  if (q === "en" || q === "zh" || q === "zh-hant" || q === "vi") return q;
  const n = (navigator.language || "en").toLowerCase();
  if (n.startsWith("vi")) return "vi";
  if (/^zh-(hant|tw|hk|mo)/.test(n)) return "zh-hant";
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === "Asia/Ho_Chi_Minh" || tz === "Asia/Saigon") return "vi";
  } catch { /* ignore */ }
  return n.startsWith("zh") ? "zh" : "en";
})();
const tx = (k: string) => TEXT[lang][k] ?? TEXT.en[k] ?? k;
document.documentElement.lang = lang === "zh" ? "zh-Hans" : lang === "zh-hant" ? "zh-Hant" : lang;

function themeName(p: Puzzle): string {
  if (lang === "zh") return p.theme.zh;
  if (lang === "en") return p.theme.en;
  if (lang === "vi" && p.theme.vi) return p.theme.vi;
  return THEME[p.theme.en]?.[lang] ?? (lang === "zh-hant" ? p.theme.zh : p.theme.en);
}
function hintText(p: Puzzle): string | null {
  if (!p.hint) return null;
  if (lang === "vi") return p.hint.vi ?? p.hint.en;
  return lang === "zh" || lang === "zh-hant" ? p.hint.zh : p.hint.en;
}

const askedId = params.get("id");
const isDaily = !askedId || !PUZZLES.some((p) => p.id === askedId);
const puzzle: Puzzle = isDaily ? puzzleFor() : PUZZLES.find((p) => p.id === askedId)!;

/** Every link out says it came from a widget, and from whose page. */
function tagged(url: string): string {
  const u = new URL(url);
  u.searchParams.set("utm_source", "embed");
  u.searchParams.set("utm_medium", "widget");
  let host = "unknown";
  try { if (document.referrer) host = new URL(document.referrer).hostname; } catch { /* no referrer */ }
  u.searchParams.set("utm_campaign", host);
  return u.toString();
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, props: Record<string, string> = {}, ...kids: (Node | string)[]) => {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(props)) n.setAttribute(k, v);
  n.append(...kids);
  return n;
};

const boardHost = el("div", { class: "board-wrap" });
const status = el("div", { class: "status", role: "status", "aria-live": "polite" }, tx("prompt"));
const hintBtn = el("button", { type: "button", class: "btn" }, tx("hint"));
const resetBtn = el("button", { type: "button", class: "btn" }, tx("reset"));
const appUrl = `https://play.xiangqimaster.com/learn/puzzles/${encodeURIComponent(puzzle.id)}?lang=${APP_LANG[lang]}`;
const more = el("a", { class: "btn more", href: tagged(appUrl), target: "_blank", rel: "noopener" }, tx("more"), " →");
const brand = el("a", { href: tagged(SITE_HOME[lang]), target: "_blank", rel: "noopener" }, "Xiangqi Master");

document.getElementById("root")!.replaceChildren(
  el("div", { class: "head" },
    el("b", {}, isDaily ? `${tx("daily")} · ${today()}` : themeName(puzzle)),
    isDaily ? el("span", {}, themeName(puzzle)) : "",
  ),
  boardHost,
  status,
  el("div", { class: "row" }, hintBtn, resetBtn, more),
  el("div", { class: "foot" }, tx("by"), " ", brand),
);

let pos: Position = parseFen(puzzle.fen);
let lastMove: Move | null = null;
let solved = false;
let hintSquare: number | null = null;

const board = new BoardView(boardHost, onMove);
board.orientation = Color.Red;
render();

function render() {
  board.setState({ pos, lastMove, selectable: solved ? null : Color.Red, viewer: Color.Red, showMovesFor: hintSquare, hideDanger: true });
}
function say(text: string, kind: "" | "ok" | "bad" = "") {
  status.textContent = text;
  status.className = "status" + (kind ? " " + kind : "");
}

/** The same test PuzzleView makes, so the two can never disagree. */
function onMove(m: Move) {
  if (solved) return;
  const before = toFen(pos);
  pos.makeMove(m);
  lastMove = m;
  const ok = puzzle.task.kind === "mate" ? isCheckmate(pos) : puzzle.task.solution.includes(moveToUci(m));
  if (ok) {
    solved = true;
    hintSquare = null;
    say("✓ " + tx("right"), "ok");
    render();
    return;
  }
  say("✗ " + tx("wrong"), "bad");
  render();
  setTimeout(() => { pos = parseFen(before); lastMove = null; render(); }, 650);
}

hintBtn.addEventListener("click", () => {
  if (solved) return;
  if (puzzle.task.kind === "move") hintSquare = coordToSquare(puzzle.task.solution[0]!.slice(0, 2));
  else {
    const mv = legalMoves(pos).find((m) => { const n = pos.clone(); n.makeMove(m); return isCheckmate(n); });
    hintSquare = mv ? mv.from : null;
  }
  const h = hintText(puzzle);
  if (h) say(h);
  render();
});
resetBtn.addEventListener("click", () => {
  pos = parseFen(puzzle.fen);
  lastMove = null; solved = false; hintSquare = null;
  say(tx("prompt"));
  render();
});
